import React, { useState } from "react";
import {
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  InputNumber,
  Checkbox,
  Button,
  message,
  TimePicker,
  Avatar,
} from "antd";
import { post } from "../../services/ApiRouting";
import moment from "moment";
import { BsBuildingsFill } from "react-icons/bs";

const { TextArea } = Input;
const { Option } = Select;

// Common input styles
const inputStyles = {
  borderRadius: "6px",
  border: "1px solid #d9d9d9",
  padding: "8px 12px",
  transition: "all 0.3s ease",
};

const labelStyles = {
  fontWeight: "500",
  color: "#333",
  fontSize: "14px",
  marginBottom: "6px",
};

const EnquiryModal = ({ visible, onCancel, vendorDetails }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const eventTypes = [
    "Wedding",
    "Reception",
    "Engagement",
    "Mehendi",
    "Haldi",
    "Sangeet",
    "Birthday",
    "Anniversary",
    "Corporate Event",
    "Other",
  ];

  const budgetRanges = [
    "Under ₹50,000",
    "₹50,000 - ₹1,00,000",
    "₹1,00,000 - ₹2,00,000",
    "₹2,00,000 - ₹5,00,000",
    "₹5,00,000 - ₹10,00,000",
    "Above ₹10,00,000",
  ];

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      // Format the data for API
      const enquiryData = {
        vendor_id: vendorDetails?.id,
        customer_name: values.customer_name,
        customer_email: values.customer_email,
        customer_phone: values.customer_phone,
        customer_whatsapp: values.customer_whatsapp,
        event_type: values.event_type,
        event_date: values.event_date.format("YYYY-MM-DD"),
        event_time: values.event_time
          ? values.event_time.format("HH:mm")
          : null,
        expected_guests: values.expected_guests,
        event_location: values.event_location,
        service_requirements: values.service_requirements,
        budget_range: values.budget_range,
        special_requests: values.special_requests,
        catering_required: values.catering_required || false,
        decoration_required: values.decoration_required || false,
        video_required: values.video_required || false,
        vegetarian_only: values.vegetarian_only || false,
        vendor_category: vendorDetails?.category?.name || null,
        status: "pending",
      };

      const response = await post("vendor-enquiries", enquiryData);

      if (response.success) {
        message.success(
          "Enquiry submitted successfully! We will contact you soon."
        );
        form.resetFields();
        onCancel();
      } else {
        message.error(response.message || "Failed to submit enquiry");
      }
    } catch (error) {
      console.error("Enquiry submission error:", error);
      if (error.response?.data?.errors) {
        // Handle validation errors
        const errorMessages = Object.values(error.response.data.errors).flat();
        errorMessages.forEach((msg) => message.error(msg));
      } else {
        message.error("Failed to submit enquiry. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={
        <div
          style={{
            textAlign: "center",
            fontSize: "24px",
            fontWeight: "600",
            color: "var(--primaryColor)",
            borderBottom: "2px solid var(--primaryColor)",
            paddingBottom: "10px",
            marginBottom: "20px",
          }}
        >
          Request Quote
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={1200}
      centered
      style={{ top: 20 }}
      bodyStyle={{
        padding: "24px 32px",
        maxHeight: "80vh",
        overflowY: "auto",
      }}
    >
      {/* Vendor Info Header */}
      {vendorDetails && (
        <div
          style={{
            background: "#eee",
            color: "#000",
            padding: "16px 20px",
            borderRadius: "8px",
            marginBottom: "24px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <div style={{ fontSize: "24px" }}>
            <Avatar
              icon={<BsBuildingsFill />}
              shape="square"
              size={40}
              src={vendorDetails.logo}
            />
          </div>
          <div>
            <div style={{ fontWeight: "600", fontSize: "16px" }}>
              {vendorDetails.name}
            </div>
            <div style={{ fontSize: "14px", opacity: 0.9 }}>
              {vendorDetails.based_area}, {vendorDetails.city}
            </div>
          </div>
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          event_date: moment(),
          catering_required: false,
          decoration_required: false,
          video_required: false,
          vegetarian_only: false,
        }}
        style={{
          background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
          padding: "24px",
          borderRadius: "12px",
          border: "1px solid #e8e9ea",
        }}
      >
        <div style={{ display: "flex", gap: "32px" }}>
          {/* Left Column */}
          <div
            style={{
              flex: 1,
              background: "white",
              padding: "20px",
              borderRadius: "8px",
            }}
          >
            <div className="row">
              <Form.Item
                name="customer_name"
                label={<span style={labelStyles}>Full Name *</span>}
                rules={[
                  { required: true, message: "Please enter your full name" },
                ]}
              >
                <Input placeholder="Enter your full name" style={inputStyles} />
              </Form.Item>

              <div className="col-md-6">
                <Form.Item
                  name="customer_email"
                  label="Email"
                  rules={[
                    { required: true, message: "Please enter your email" },
                    { type: "email", message: "Please enter a valid email" },
                  ]}
                >
                  <Input placeholder="Enter your email" style={inputStyles} />
                </Form.Item>
              </div>
              <div className="col-md-6">
                <Form.Item
                  name="customer_phone"
                  label="Phone Number"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your phone number",
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter your phone number"
                    style={inputStyles}
                  />
                </Form.Item>
              </div>

              <Form.Item name="customer_whatsapp" label="WhatsApp (Optional)">
                <Input
                  placeholder="Enter your WhatsApp number"
                  style={inputStyles}
                />
              </Form.Item>

              <Form.Item
                name="event_type"
                label="Event Type"
                rules={[
                  { required: true, message: "Please select event type" },
                ]}
              >
                <Select placeholder="Select event type">
                  {eventTypes.map((type) => (
                    <Option key={type} value={type}>
                      {type}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="event_date"
                label="Event Date"
                rules={[
                  { required: true, message: "Please select event date" },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="Select event date"
                  disabledDate={(current) =>
                    current && current < moment().startOf("day")
                  }
                />
              </Form.Item>

              <Form.Item name="event_time" label="Event Time (Optional)">
                <TimePicker
                  style={{ width: "100%" }}
                  placeholder="Select event time"
                  format="HH:mm"
                />
              </Form.Item>

              <Form.Item name="expected_guests" label="Expected Guests">
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="Number of guests"
                  min={1}
                />
              </Form.Item>

              <Form.Item name="budget_range" label="Budget Range">
                <Select placeholder="Select budget range">
                  {budgetRanges.map((range) => (
                    <Option key={range} value={range}>
                      {range}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          </div>

          {/* Right Column */}
          <div
            style={{
              flex: 1,
              background: "white",
              padding: "20px",
              borderRadius: "8px",
            }}
          >
            <Form.Item name="event_location" label="Event Location">
              <Input placeholder="Enter event location" style={inputStyles} />
            </Form.Item>

            <Form.Item name="service_requirements" label="Service Requirements">
              <TextArea
                rows={4}
                placeholder="Describe your specific requirements"
                style={inputStyles}
              />
            </Form.Item>

            <Form.Item name="special_requests" label="Special Requests">
              <TextArea
                rows={4}
                placeholder="Any special requests or additional information"
                style={inputStyles}
              />
            </Form.Item>

            <div
              style={{
                marginTop: "24px",
                padding: "16px",
                background: "linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)",
                borderRadius: "8px",
                border: "1px solid #e6f0ff",
              }}
            >
              <h4
                style={{
                  marginBottom: "16px",
                  color: "var(--primaryColor)",
                  fontSize: "16px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                ✨ Additional Services
              </h4>
              <Form.Item name="catering_required" valuePropName="checked">
                <Checkbox>Catering Required</Checkbox>
              </Form.Item>

              <Form.Item name="decoration_required" valuePropName="checked">
                <Checkbox>Decoration Required</Checkbox>
              </Form.Item>

              <Form.Item name="video_required" valuePropName="checked">
                <Checkbox>Video Coverage Required</Checkbox>
              </Form.Item>

              <Form.Item name="vegetarian_only" valuePropName="checked">
                <Checkbox>Vegetarian Only</Checkbox>
              </Form.Item>
            </div>
          </div>
        </div>

        <div
          style={{
            marginTop: "32px",
            textAlign: "center",
            padding: "20px",
            background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
            borderRadius: "8px",
            border: "1px solid #e8e9ea",
          }}
        >
          <Button
            onClick={handleCancel}
            style={{
              marginRight: "16px",
              padding: "8px 24px",
              height: "40px",
              borderRadius: "6px",
              border: "1px solid #d9d9d9",
              color: "#666",
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            style={{
              backgroundColor: "var(--primaryColor)",
              borderColor: "var(--primaryColor)",
              padding: "8px 32px",
              height: "40px",
              borderRadius: "6px",
              fontWeight: "500",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            }}
          >
            Submit Enquiry
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default EnquiryModal;
